import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { WritableSelect } from '../../components/ui/writable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import type { CarBrand, CarModel } from '../../types/car';
import { useLanguage } from '../../contexts/LanguageContext';

const agreementFormSchema = z.object({
  carBrand: z.string().min(1, 'Car brand is required'),
  carModel: z.string().min(1, 'Car model is required'),
  carYear: z.number().min(1900).max(new Date().getFullYear() + 1),
  carColor: z.string().optional(),
  carChassisNo: z.string().optional(),
  carPlateNo: z.string().optional(),
  sellerName: z.string().min(1, 'Seller name is required'),
  sellerIdNo: z.string().min(1, 'Seller ID is required'),
  sellerAddress: z.string().optional(),
  sellerContact: z.string().min(1, 'Seller contact is required'),
  buyerName: z.string().min(1, 'Buyer name is required'),
  buyerIdNo: z.string().min(1, 'Buyer ID is required'),
  buyerAddress: z.string().optional(),
  buyerContact: z.string().min(1, 'Buyer contact is required'),
  agreedPrice: z.union([z.string(), z.number()]).transform((v) => (v === '' || v == null ? undefined : typeof v === 'string' ? parseFloat(v) : v)).optional(),
  currency: z.enum(['IQD', 'USD']).optional(),
  feeFromSeller: z.coerce.number().default(0),
  feeFromBuyer: z.coerce.number().default(0),
  notes: z.string().optional(),
});

type AgreementFormData = z.infer<typeof agreementFormSchema>;

export default function AddAgreementPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AgreementFormData>({
    resolver: zodResolver(agreementFormSchema) as any,
    defaultValues: {
      carBrand: '',
      carModel: '',
      carYear: new Date().getFullYear(),
      currency: 'USD',
      feeFromSeller: 0,
      feeFromBuyer: 0,
    },
  });

  const currentBrandId = selectedBrand ? brands.find((b) => b.name === selectedBrand)?.id ?? 0 : 0;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [brandsRes, modelsRes] = await Promise.all([
          api.get('/api/brands'),
          api.get('/api/models'),
        ]);
        setBrands(Array.isArray(brandsRes) ? brandsRes : []);
        setModels(Array.isArray(modelsRes) ? modelsRes : []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchAll();
  }, []);

  const createBrand = async (name: string) => {
    const newBrand = await api.post('/api/brands', { name, active: true });
    setBrands((prev) => [...prev, newBrand]);
    setSelectedBrand(newBrand.name);
    setValue('carBrand', newBrand.name);
    setSelectedModel('');
    setValue('carModel', '');
  };

  const createModel = async (name: string) => {
    const newModel = await api.post('/api/models', { name, brandId: currentBrandId, active: true });
    setModels((prev) => [...prev, newModel]);
    setSelectedModel(newModel.name);
    setValue('carModel', newModel.name);
  };

  const handleBrandChange = (name: string) => {
    setSelectedBrand(name);
    setValue('carBrand', name);
    setSelectedModel('');
    setValue('carModel', '');
  };

  const handleModelChange = (name: string) => {
    setSelectedModel(name);
    setValue('carModel', name);
  };

  const onSubmit = async (data: AgreementFormData) => {
    try {
      await api.post('/api/agreements', {
        carBrand: data.carBrand,
        carModel: data.carModel,
        carYear: data.carYear,
        carColor: data.carColor || undefined,
        carChassisNo: data.carChassisNo || undefined,
        carPlateNo: data.carPlateNo || undefined,
        sellerName: data.sellerName,
        sellerIdNo: data.sellerIdNo,
        sellerAddress: data.sellerAddress || undefined,
        sellerContact: data.sellerContact,
        buyerName: data.buyerName,
        buyerIdNo: data.buyerIdNo,
        buyerAddress: data.buyerAddress || undefined,
        buyerContact: data.buyerContact,
        agreedPrice: data.agreedPrice ?? undefined,
        currency: data.currency || 'USD',
        feeFromSeller: data.feeFromSeller ?? 0,
        feeFromBuyer: data.feeFromBuyer ?? 0,
        notes: data.notes || undefined,
      });
      toast.success(t('agreement.recorded'));
      navigate('/agreements');
    } catch (err: unknown) {
      toast.error((err as { error?: string })?.error ?? t('agreement.createError'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild className="border-slate-200">
              <Link to="/agreements">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('agreement.backToAgreements')}
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('agreement.add')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">{t('agreement.subtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle>{t('agreement.details')}</CardTitle>
            <CardDescription>{t('agreement.detailsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">{t('agreement.car')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>{t('agreement.brand')} *</Label>
                    <WritableSelect
                      value={selectedBrand}
                      onValueChange={handleBrandChange}
                      options={brands}
                      onCreateNew={createBrand}
                      placeholder="e.g. Toyota"
                    />
                    {errors.carBrand && <p className="text-sm text-red-600">{errors.carBrand.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.model')} *</Label>
                    <WritableSelect
                      value={selectedModel}
                      onValueChange={handleModelChange}
                      options={models.filter((m) => m.brandId === currentBrandId)}
                      onCreateNew={createModel}
                      placeholder="e.g. Camry"
                      disabled={!currentBrandId}
                    />
                    {errors.carModel && <p className="text-sm text-red-600">{errors.carModel.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.year')} *</Label>
                    <Input type="number" {...register('carYear', { valueAsNumber: true })} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.color')}</Label>
                    <Input {...register('carColor')} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.plateNumber')}</Label>
                    <Input {...register('carPlateNo')} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.chassisNumber')}</Label>
                    <Input {...register('carChassisNo')} />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">{t('agreement.sellerSection')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>{t('agreement.name')} *</Label>
                    <Input {...register('sellerName')} />
                    {errors.sellerName && <p className="text-sm text-red-600">{errors.sellerName.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.idNumber')} *</Label>
                    <Input {...register('sellerIdNo')} />
                    {errors.sellerIdNo && <p className="text-sm text-red-600">{errors.sellerIdNo.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.contact')} *</Label>
                    <Input {...register('sellerContact')} />
                    {errors.sellerContact && <p className="text-sm text-red-600">{errors.sellerContact.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.address')}</Label>
                    <Input {...register('sellerAddress')} />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">{t('agreement.buyerSection')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>{t('agreement.name')} *</Label>
                    <Input {...register('buyerName')} />
                    {errors.buyerName && <p className="text-sm text-red-600">{errors.buyerName.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.idNumber')} *</Label>
                    <Input {...register('buyerIdNo')} />
                    {errors.buyerIdNo && <p className="text-sm text-red-600">{errors.buyerIdNo.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.contact')} *</Label>
                    <Input {...register('buyerContact')} />
                    {errors.buyerContact && <p className="text-sm text-red-600">{errors.buyerContact.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.address')}</Label>
                    <Input {...register('buyerAddress')} />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">{t('agreement.dealFees')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label>{t('agreement.agreedPrice')}</Label>
                    <Input type="number" step="any" {...register('agreedPrice')} placeholder="0" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.currency')}</Label>
                    <Select value={watch('currency') || 'USD'} onValueChange={(v) => setValue('currency', v as 'IQD' | 'USD')}>
                      <SelectTrigger className="border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="IQD">IQD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.feeFromSeller')}</Label>
                    <Input type="number" step="any" {...register('feeFromSeller')} placeholder="0" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.feeFromBuyer')}</Label>
                    <Input type="number" step="any" {...register('feeFromBuyer')} placeholder="0" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('agreement.notes')}</Label>
                    <Input {...register('notes')} />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium">
                  {t('agreement.save')}
                </Button>
                <Button type="button" variant="outline" asChild className="border-slate-200">
                  <Link to="/agreements">{t('common.cancel')}</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
